import type { CorrectionLetterReasonEntry, ReasonSelection } from "@credit-acceptance/step6-bff-client";
import { clsx } from "clsx";
import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { getErrorMessage } from "../../../../lib/query/errors";
import { useCorrectionLetter } from "./api/useCorrectionLetter";
import { useSaveCorrectionLetter } from "./api/useSaveCorrectionLetter";
import { LOCKED_CORRECTION_REASON_CODES } from "./correctionLetter.constants";

interface CorrectionLetterModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  readOnly: boolean;
}

const byDisplayOrder = (a: CorrectionLetterReasonEntry, b: CorrectionLetterReasonEntry) =>
  (a.displayOrder ?? 0) - (b.displayOrder ?? 0);

// Static placeholder for the third-party section (see TODO in CorrectionLetterReasons).
const THIRD_PARTY_CHANGES = [
  "vscAdministratorChanged",
  "vscMileageTermIncreased",
  "vscMonthsTermIncreased",
  "gapAdministratorChanged",
] as const;

interface CorrectionLetterReasonsProperties {
  approvalId: string;
  reasons: CorrectionLetterReasonEntry[];
  onSaved: () => void;
  readOnly: boolean;
}

// Mounted only once the GET resolves, so the checkbox state can be seeded
// straight from the response. The dialog unmounts its content on close, so
// reopening re-seeds from the refetched server state.
function CorrectionLetterReasons({ approvalId, reasons, onSaved, readOnly }: CorrectionLetterReasonsProperties) {
  const { t } = useTranslation();
  const sorted = [...reasons].sort(byDisplayOrder);
  // Reasons flagged `thirdPartyAncillary` are excluded for now (see TODO below).
  const selectableReasons = sorted.filter((reason) => !reason.thirdPartyAncillary);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(selectableReasons.flatMap((reason) => (reason.selected && reason.code ? [reason.code] : [])))
  );

  const saveCorrectionLetter = useSaveCorrectionLetter(approvalId);

  const toggleReason = (code: string, checked: boolean) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  // Sends the desired state for every selectable reason (checked and
  // unchecked), so deselecting a previously saved reason clears it server-side.
  const handleApply = () => {
    const selections: ReasonSelection[] = selectableReasons.flatMap((reason) =>
      reason.code ? [{ code: reason.code, selected: selected.has(reason.code) }] : []
    );
    saveCorrectionLetter.mutate(selections, { onSuccess: onSaved });
  };

  return (
    <>
      <div className="gap-(--spacing-4) p-(--spacing-6) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) flex flex-col">
        <div className="gap-(--spacing-2) flex flex-col">
          {selectableReasons.map((reason) => {
            const code = reason.code ?? "";
            const disabled = readOnly || LOCKED_CORRECTION_REASON_CODES.has(code);
            return (
              <label
                key={code}
                className={clsx(
                  "gap-(--spacing-3) flex items-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer"
                )}
              >
                <Checkbox
                  className="bg-white"
                  checked={selected.has(code)}
                  disabled={disabled}
                  onCheckedChange={(checked) => toggleReason(code, checked === true)}
                />
                <span>{reason.description}</span>
              </label>
            );
          })}
        </div>

        {/* TODO: Add the "Third Party Ancillary Product Information changed from RISC"
            section for reasons flagged `thirdPartyAncillary`. Need to understand
            exactly what we need to show here before building it. Until then this
            is a static, always-disabled placeholder list. */}
        <div className="gap-(--spacing-2) flex flex-col">
          <p className="font-(--font-weight-semibold)">{t("financial.correctionLetterModal.thirdPartyHeading")}</p>
          {THIRD_PARTY_CHANGES.map((key) => (
            <label key={key} className="gap-(--spacing-3) flex cursor-not-allowed items-center">
              <Checkbox className="bg-white" checked={false} disabled />
              <span>{t(`financial.correctionLetterModal.thirdPartyChanges.${key}`)}</span>
            </label>
          ))}
        </div>
      </div>

      {saveCorrectionLetter.isError && (
        <p role="alert" className="text-destructive px-(--spacing-6) pb-(--spacing-2) text-xs">
          {t("financial.correctionLetterModal.saveError", { message: getErrorMessage(saveCorrectionLetter.error) })}
        </p>
      )}

      <DialogFooter className="gap-(--spacing-2) px-(--spacing-4) py-(--spacing-2) justify-center border-t sm:justify-center">
        {!readOnly && (
          <Button type="button" size="xs" disabled={saveCorrectionLetter.isPending} onClick={handleApply}>
            {saveCorrectionLetter.isPending && <Loader2 aria-hidden="true" className="size-3 animate-spin" />}
            {t("financial.correctionLetterModal.apply")}
          </Button>
        )}
        <DialogClose asChild>
          <Button type="button" size="xs" variant="outline" disabled={saveCorrectionLetter.isPending}>
            {t("financial.correctionLetterModal.cancel")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  );
}

// Opened from the "Correction Letter" link in the Financial section header.
function CorrectionLetterModal({ open, onOpenChange, approvalId, readOnly }: CorrectionLetterModalProperties) {
  const { t } = useTranslation();
  const { data, dataUpdatedAt, isLoading, isError, error } = useCorrectionLetter(approvalId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden bg-white sm:max-w-lg"
      >
        <div className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex items-center justify-between">
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold)">
            {t("financial.correctionLetterModal.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("financial.correctionLetterModal.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-0-5) transition-colors">
            <X className="size-4.5" aria-hidden="true" />
            <span className="sr-only">{t("financial.correctionLetterModal.close")}</span>
          </DialogClose>
        </div>

        {isLoading && (
          <div className="text-muted-foreground gap-(--spacing-1) p-(--spacing-6) flex items-center text-xs">
            <Loader2 aria-hidden="true" className="size-3 animate-spin" />
            {t("financial.correctionLetterModal.loading")}
          </div>
        )}

        {isError && (
          <p role="alert" className="text-destructive p-(--spacing-6) text-xs">
            {t("financial.correctionLetterModal.loadError", { message: getErrorMessage(error) })}
          </p>
        )}

        {/* Keyed on dataUpdatedAt so a background refetch re-seeds the checkboxes. */}
        {data && (
          <CorrectionLetterReasons
            key={dataUpdatedAt}
            approvalId={approvalId}
            reasons={data.reasons ?? []}
            onSaved={() => onOpenChange(false)}
            readOnly={readOnly}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CorrectionLetterModal;
